import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/KStackAuthContext';
import ThemeToggle from '../components/ThemeToggle';
import './Login.css';

interface LoginRouterState {
  // F4: pages that redirect into /login (verify-email success,
  // reset-password success) attach a one-shot success message here
  // so the user sees confirmation in the place they need to act
  // next instead of a separate toast layer.
  info?: string;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const routerState = (location.state ?? {}) as LoginRouterState;
  // One-shot info banner (e.g. "Email verified — sign in…") from F4
  // page redirects. setter is intentionally unused — the banner
  // stays visible until the user navigates away.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [info, _setInfo] = useState<string | null>(routerState.info || null);
  const { signIn, signInWithGoogle, error, clearError } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Read directly from the DOM first (handles browser autofill / password-manager
    // fills that bypass React's onChange), then fall back to controlled state.
    // 2026-05-27: removed the truthy-pair pre-check that lived here — it was
    // rejecting legitimate submits where React's controlled `value={password}`
    // had forced the DOM input back to "" between renders, leaving both DOM
    // read and state empty even when the user could see characters in the
    // field. The check was rejecting a state where the only sensible action
    // is to let the server validate. The server returns 400/401 on empty
    // credentials and we surface that the same way as any other auth error.
    const form = e.currentTarget;
    const emailInput = form.elements.namedItem('email') as HTMLInputElement | null;
    const passwordInput = form.elements.namedItem('password') as HTMLInputElement | null;

    const finalEmail = (emailInput?.value || email || '').trim();
    const finalPassword = passwordInput?.value || password || '';

    setIsLoading(true);
    setLocalError(null);

    try {
      await signIn(finalEmail, finalPassword);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      setLocalError(errorMessage);
      // BOLUS_NOTES-4: keep the email the user typed (autofilled values never
      // reached React state, so the controlled input blanked it on re-render);
      // clear only the password.
      setEmail(finalEmail);
      setPassword('');
    } finally {
      // Small delay before re-enabling to prevent double-submit
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const handleGoogleSignIn = () => {
    setIsLoading(true);
    setLocalError(null);
    signInWithGoogle();
  };

  const displayError = localError || error;

  return (
    <div className="login-container">
      <ThemeToggle placement="corner" />
      <div className="login-box">
        <div className="login-header">
          <h1>BolusNotes Web</h1>
        </div>
        <p>Sign in to access your diabetes management dashboard</p>

        {info && (
          <div className="banner-info">
            <p>{info}</p>
          </div>
        )}

        {displayError && (
          <div className="error-modal" onClick={() => { setLocalError(null); clearError(); }}>
            <div className="error-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-message">❌ {displayError}</div>
              <button 
                className="error-ok-button"
                onClick={() => { setLocalError(null); clearError(); }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <label htmlFor="password">Password</label>
              <Link
                to="/forgot-password"
                className="forgot-password-link"
                style={{ fontSize: 13 }}
              >
                Forgot password?
              </Link>
            </div>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '🔒'}
              </button>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="checkbox-input"
                disabled={isLoading}
              />
              <span className="checkbox-custom"></span>
              Remember me
            </label>
          </div>

          <button 
            type="submit" 
            disabled={isLoading} 
            className="btn-primary"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="btn-google"
        >
          {isLoading ? '🔄 Connecting...' : '🌐 Sign in with Google'}
        </button>

        <div className="signup-prompt" style={{ marginTop: 24, textAlign: 'center' }}>
          <p style={{ margin: 0 }}>
            New to BolusNotes?{' '}
            <Link to="/signup" className="signup-link">
              Create an account
            </Link>
          </p>
        </div>

        <div className="security-badge">
          🔐 <strong>Powered by K-Stack:</strong> Secure, cross-platform authentication
        </div>
      </div>
    </div>
  );
};

export default Login;

